<template>
  <div class="configurations-table">
    <mi-table
      ref="miTable"
      :columns="table.columns"
      :filter="configurationsSearchString"
      :loading="areConfigurationsLoading || isPinLoading"
      :rows="table.rows"
      :custom-header="true"
      :on-change-sort-table-column-visibility="tableCallbacks.onChangeSortTableColumnVisibility"
      :on-change-sort-table-direction="tableCallbacks.onChangeSortTableDirection"
      :on-row-click="handleRowClick"
      class="configurations-table q-px-lg q-pb-lg"
      column-sort-order="da"
      loading-label="Loading configurations. Please wait ..."
      no-data-label="No configurations found"
      row-key="id"
      hide-pagination
      sticky-header
      virtual-scroll
      striped
    >
      <template #top>
        <configurations-header
          :configurations-search-string="configurationsSearchString"
          :is-pin-filter-active="isPinFilterActive"
          :master-checkbox-state="masterCheckboxState"
          :has-any-selected="hasAnySelected"
          :selected-count="configurationsSelected.length"
          @toggle-pin-filter="togglePinFilter"
          @search-input="handleSearchInput"
          @master-checkbox-click="handleMasterCheckboxClick"
          @select-all="handleSelectAllCurrentPage"
          @select-mine="handleSelectMineOnly"
          @select-from-today="handleSelectFromToday"
          @select-pinned="handleSelectPinned"
          @create-new-list="showSaveNewListModal = true"
          @add-to-list="showAddConfigurationsToListModal = true"
          @navigate-saved-lists="$router.push({ name: PAC_SAVED_LISTS_ROUTE.name })"
          @new-configuration="showNewConfigurationDialog = true"
        ></configurations-header>
      </template>

      <!-- Name column -->
      <template #body-cell-name="{ value, row }">
        <td class="text-left configurations-table__clickable-cell">
          <div class="flex items-center no-wrap">
            <mi-checkbox
              :model-value="isSelected(row.id)"
              class="q-pr-sm"
              :class="{ 'configurations-table__checkbox--unselected': !isSelected(row.id) }"
              @click.stop="toggleSelection(row.id)"
            ></mi-checkbox>
            <span>{{ value }}</span>
            <mi-btn
              v-if="!row.pinned"
              flat
              dense
              class="configurations-table__pin-btn"
              @click.stop="handleTogglePin(row)"
            >
              <img
                src="@/assets/images/pin-outline.svg"
                alt="pin"
                width="16"
                height="16"
              />
            </mi-btn>
            <mi-btn
              v-else
              flat
              dense
              class="configurations-table__pin-btn configurations-table__pin-btn--pinned"
              @click.stop="handleTogglePin(row)"
            >
              <img
                src="@/assets/images/pin-dark.svg"
                alt="unpin"
                width="16"
                height="16"
              />
            </mi-btn>
          </div>
        </td>
      </template>

      <!-- Description column -->
      <template #body-cell-description="{ value, row }">
        <mi-td class="configurations-table__clickable-cell">
          <div class="flex items-center no-wrap">
            <span
              class="configurations-table__description ellipsis-2-lines q-mr-xs relative-position text-left"
            >
              {{ value }}
            </span>
            <div class="flex no-wrap q-ml-auto configurations-table__row-actions" @click.stop>
              <mi-btn color="transparent">
                <q-icon class="text-dark" size="small">
                  <img
                    src="@/assets/images/dots_vertical.svg"
                    alt="Configuration actions"
                  />
                </q-icon>
                <mi-menu>
                  <q-list>
                    <q-item
                      v-close-popup
                      clickable
                      @click="handleTogglePin(row)"
                    >
                      <q-item-section>
                        <q-item-label>
                          {{ row.pinned ? 'Unpin configuration' : 'Pin configuration' }}
                        </q-item-label>
                      </q-item-section>
                    </q-item>
                    <q-item
                      v-close-popup
                      clickable
                      :class="{ 'disabled': !hasPermissionToDelete(row) }"
                      @click="openDeleteConfigurationDialog(row)"
                    >
                      <q-item-section>
                        <q-item-label>
                          Delete
                        </q-item-label>
                      </q-item-section>
                    </q-item>
                  </q-list>
                </mi-menu>
              </mi-btn>
            </div>
          </div>
        </mi-td>
      </template>
    </mi-table>
  </div>
  <div
    v-if="!!table.rows.length"
    class="configurations-table__pagination"
  >
    <mi-pagination
      :model-value="pagination.currentPage"
      :max="pagination.totalPages"
      :max-pages="pagination.maxNavPage"
      size="14px"
      boundary-links
      direction-links
      boundary-numbers
      ellipses
      @update:model-value="handlePaginationChange"
    ></mi-pagination>
  </div>
  <!-- Configuration delete dialog -->
  <configuration-delete-dialog
    v-model="isDeleteConfigurationDialogShown"
    :configuration="configurationToDelete"
    @delete:configuration="handleConfigurationDeletedEvent"
  ></configuration-delete-dialog>

  <learn-more-pac-modal :show-modal="showLearnMoreModal" @update:show-modal="showLearnMoreModal = false">
  </learn-more-pac-modal>

  <save-new-list-modal
    :show-modal="showSaveNewListModal"
    :selected-configurations-ids-set="configurationsSelected"
    :handle-deselect-all="handleDeselectAll"
    @update:show-modal="showSaveNewListModal = false"
  >
  </save-new-list-modal>
  <add-configurations-to-list-modal
    :handle-deselect-all="handleDeselectAll"
    :selected-configurations-ids-set="configurationsSelected"
    :show-modal="showAddConfigurationsToListModal"
    @update:show-modal="showAddConfigurationsToListModal = false"
  >
  </add-configurations-to-list-modal>

  <!-- New configuration dialog -->
  <mi-dialog
    v-model="showNewConfigurationDialog"
    :close-icon="true"
    class="new-configuration-dialog"
    persistent
  >
    <div class="new-configuration-step-description">
      <h5 class="q-mt-none q-mb-sm"> New configuration </h5>
      <p class="q-mb-none"> {{ stepsDescriptions[currentStep] }}</p>
    </div>
    <new-configuration-stepper
      v-if="showNewConfigurationDialog"
      @update:model-value="val => currentStep = val"
      @create:configuration="handleConfigurationCreatedEvent"
    ></new-configuration-stepper>
  </mi-dialog>
</template>

<script>
  import { computed, onMounted, onUnmounted, ref, watch, getCurrentInstance } from 'vue'
  import { useStore } from 'vuex'
  import { useRouter } from 'vue-router'
  import { usePagination } from '@/composables/usePagination'
  import { useTable } from '@/composables/useTable'
  import ConfigurationDeleteDialog from '@/components/pac/configuration/dialogs/ConfigurationDeleteDialog.vue'
  import MiPagination from '@/packages/@mi-library/MiPagination/MiPagination.vue'
  import { getConfigurations, togglePinConfiguration } from '@/api'
  import { configurationsTableColumns } from '@/components/pac/configurations/utils'
  import { PAC_DELETED_CONFIG } from '@/utils/analytics/constants'
  import { PAC_CONFIGURATION_ROUTE, PAC_SAVED_LISTS_ROUTE } from '@/router/routeNames'
  import { SET_CONFIGURATIONS_SEARCH_STRING } from '@/store/modules/pac/mutationTypes'
  import recordAnalytics from '@/utils/analytics/recordAnalytics'
  import MiBtn from '@/packages/@mi-library/MiBtn/MiBtn.vue'
  import MiCheckbox from '@/packages/@mi-library/MiCheckbox/MiCheckbox.vue'
  import MiMenu from '@/packages/@mi-library/MiMenu/MiMenu.vue'
  import SaveNewListModal from '@/components/pac/saved-lists/SaveNewListModal.vue'
  import AddConfigurationsToListModal from '@/components/pac/saved-lists/AddConfigurationsToListModal.vue'
  import NewConfigurationStepper from '@/components/pac/new-configuration-stepper/NewConfigurationStepper.vue'
  import { NEW_CONFIGURATION_STEPS } from '@/constants/pac'
  import ConfigurationsHeader from '@/components/pac/configurations/ConfigurationsHeader.vue'
  import LearnMorePacModal from '../LearnMorePacModal.vue'

  export default {
    name: 'ConfigurationsTable',
    components: {
      AddConfigurationsToListModal,
      SaveNewListModal,
      ConfigurationsHeader,
      MiCheckbox,
      MiBtn,
      MiMenu,
      NewConfigurationStepper,
      LearnMorePacModal,
      MiPagination,
      ConfigurationDeleteDialog
    },
    setup() {
      const areConfigurationsLoading = ref(false)
      const isPinLoading = ref(false)
      const configurationToDelete = ref(null)
      const isDeleteConfigurationDialogShown = ref(false)
      const { proxy } = getCurrentInstance()
      const showLearnMoreModal = ref(
        proxy.$q.localStorage.getItem(process.env.VUE_APP_STORAGE_KEY_LEARN_MORE_PAC_MODAL) === null
      )

      const savedListsModal = { SAVE: { key: 'saveNew' }, UPDATE: { key: 'addConfigurations' } }

      const configurationsSelected = ref([])
      const selectedSet = computed(() => new Set(configurationsSelected.value))
      const showSaveNewListModal = ref(false)
      const showAddConfigurationsToListModal = ref(false)
      const isPinFilterActive = ref(false)
      const showNewConfigurationDialog = ref(false)
      const currentStep = ref(1)

      const totalConfigurations = ref(0)

      const { pagination, handleChange: handlePaginationChange } = usePagination({ pageSize: 20 })
      const { table, callbacks: tableCallbacks } = useTable(configurationsTableColumns)

      const router = useRouter()
      const { state: { user, pac: pacStore }, commit } = useStore()

      const handleConfigurationCreatedEvent = (id = '') => {
        showNewConfigurationDialog.value = false
        id && router.push({ name: PAC_CONFIGURATION_ROUTE.name, params: { id } })
      }
      const usePacMutation = name => `pac/${ name }`
      const configurationsSearchString = computed(() => pacStore.configurationsSearchString)

      const currentPageIds = computed(() => table.rows.map(r => r.id))
      const currentPageCount = computed(() => currentPageIds.value.length)

      const selectedOnCurrentPageCount = computed(() => {
        const currentPageIdSet = new Set(currentPageIds.value)
        const selectedOnCurrentPage = configurationsSelected.value.filter(id => currentPageIdSet.has(id))
        return selectedOnCurrentPage.length
      })

      const hasAnySelected = computed(() => configurationsSelected.value.length > 0)

      const hasPermissionToDelete = configuration => {
        const { authorizedUsers = [] } = configuration
        if (authorizedUsers.length) return authorizedUsers.includes(user.username)
        return true
      }

      const isSelected = id => selectedSet.value.has(id)

      const addSelection = id => {
        if (!selectedSet.value.has(id)) configurationsSelected.value = [...configurationsSelected.value, id]
      }

      const removeSelection = id => {
        if (selectedSet.value.has(id)) {
          configurationsSelected.value = configurationsSelected.value.filter(
            configSelectedId => configSelectedId !== id
          )
        }
      }

      const toggleSelection = id => {
        if (selectedSet.value.has(id)) removeSelection(id)
        else addSelection(id)
      }

      const handleSelectAllCurrentPage = () => {
        const configurationsSelectedSet = new Set(configurationsSelected.value)
        currentPageIds.value.forEach(id => configurationsSelectedSet.add(id))
        configurationsSelected.value = Array.from(configurationsSelectedSet)
      }

      const handleSelectMineOnly = () => {
        const mineIds = table.rows
          .filter(row => row.creator === user.username)
          .map(row => row.id)
        const configurationsSelectedSet = new Set(configurationsSelected.value)
        mineIds.forEach(id => configurationsSelectedSet.add(id))
        configurationsSelected.value = Array.from(configurationsSelectedSet)
      }

      const handleSelectFromToday = () => {
        const today = new Date().toISOString().slice(0, 10)
        const todayIds = table.rows
          .filter(row => row.createdDate?.startsWith(today))
          .map(row => row.id)
        const configurationsSelectedSet = new Set(configurationsSelected.value)
        todayIds.forEach(id => configurationsSelectedSet.add(id))
        configurationsSelected.value = Array.from(configurationsSelectedSet)
      }

      const handleDeselectAll = () => {
        configurationsSelected.value = []
      }

      const allCurrentPageSelected = computed(
        () => currentPageIds.value.length > 0
          && currentPageIds.value.every(id => selectedSet.value.has(id))
      )

      const masterCheckboxState = computed(() => {
        if (!hasAnySelected.value) return false
        if (allCurrentPageSelected.value) return true
        return 'indeterminate'
      })

      const handleMasterCheckboxClick = () => {
        if (allCurrentPageSelected.value) handleDeselectAll()
        else handleSelectAllCurrentPage()
      }

      const buildUrlSearchParams = () => {
        const params = new URLSearchParams()
        params.append('page', pagination.currentPage - 1)
        params.append('size', pagination.pageSize)
        params.append('sortBy', table.sort?.sortBy || 'modifiedDate')
        params.append('sortDirection', table.sort?.sortDirection || 'DESC')
        params.append('excludeFields', 'windChillData')
        params.append('excludeFields', 'ignoreList')
        params.append('excludeFields', 'restorePoints')
        params.append('excludeFields', 'selectedChoices')

        configurationsSearchString.value && params.append('search', configurationsSearchString.value)
        isPinFilterActive.value && params.append('isPinned', true)

        return params.toString()
      }

      const fetchConfigurationResults = async () => {
        areConfigurationsLoading.value = true
        const params = buildUrlSearchParams()

        const fetchConfigurationResultsPromise = () => getConfigurations(params)
        const data = await tableCallbacks.fetchTableData(fetchConfigurationResultsPromise)

        pagination.totalPages = data.totalPages || 1
        totalConfigurations.value = data.totalElements
        areConfigurationsLoading.value = false
      }

      const handleConfigurationDeletedEvent = async () => {
        await fetchConfigurationResults()
        recordAnalytics(PAC_DELETED_CONFIG)
      }

      const openDeleteConfigurationDialog = configuration => {
        configurationToDelete.value = { id: configuration.id, name: configuration.name }
        isDeleteConfigurationDialogShown.value = true
      }

      const openConfiguration = (id = '') => {
        router.push({ name: PAC_CONFIGURATION_ROUTE.name, params: { id } })
      }

      const handleRowClick = row => {
        openConfiguration(row.id)
      }

      const togglePinFilter = () => {
        isPinFilterActive.value = !isPinFilterActive.value
        pagination.currentPage === 1 ? fetchConfigurationResults() : handlePaginationChange(1)
      }

      const handleTogglePin = async row => {
        try {
          isPinLoading.value = true
          await togglePinConfiguration(row.id, !row.pinned)

          setTimeout(() => {
            fetchConfigurationResults()
            isPinLoading.value = false
          }, 500)
        }
        catch (error) {
          isPinLoading.value = false
        }
      }

      const handleSelectPinned = () => {
        const pinnedIds = table.rows
          .filter(row => row.pinned)
          .map(row => row.id)
        const configurationsSelectedSet = new Set(configurationsSelected.value)
        pinnedIds.forEach(id => configurationsSelectedSet.add(id))
        configurationsSelected.value = Array.from(configurationsSelectedSet)
      }

      const handleSearchInput = value => {
        commit(usePacMutation(SET_CONFIGURATIONS_SEARCH_STRING), value)
      }

      watch(() => pagination.currentPage, () => {
        fetchConfigurationResults()
      })

      watch(() => [configurationsSearchString.value, table.sort], () => {
        pagination.currentPage === 1 ? fetchConfigurationResults() : handlePaginationChange(1)
      })

      onMounted(async () => {
        await fetchConfigurationResults()
      })

      onUnmounted(() => {
        commit(usePacMutation(SET_CONFIGURATIONS_SEARCH_STRING), '')
      })

      return {
        table,
        pagination,
        handlePaginationChange,
        areConfigurationsLoading,
        handleConfigurationDeletedEvent,
        configurationToDelete,
        openDeleteConfigurationDialog,
        isDeleteConfigurationDialogShown,
        openConfiguration,
        handleRowClick,
        configurationsSearchString,
        tableCallbacks,
        hasPermissionToDelete,
        showLearnMoreModal,
        showAddConfigurationsToListModal,
        configurationsSelected,
        isSelected,
        toggleSelection,
        handleSelectAllCurrentPage,
        handleSelectMineOnly,
        handleSelectFromToday,
        handleDeselectAll,
        currentPageCount,
        selectedOnCurrentPageCount,
        hasAnySelected,
        masterCheckboxState,
        handleMasterCheckboxClick,
        totalConfigurations,
        showSaveNewListModal,
        buildUrlSearchParams,
        savedListsModal,
        isPinFilterActive,
        isPinLoading,
        togglePinFilter,
        handleTogglePin,
        handleSelectPinned,
        handleSearchInput,
        showNewConfigurationDialog,
        currentStep,
        handleConfigurationCreatedEvent,
        stepsDescriptions: NEW_CONFIGURATION_STEPS,
        PAC_SAVED_LISTS_ROUTE
      }
    }
  }
  </script>

<style lang="scss" scoped>
  .configurations-table {
    // stylelint-disable declaration-no-important
    min-height: 50vh;
    max-height: 75vh;

    // stylelint-disable-next-line selector-max-compound-selectors
    ::v-deep(.mi-table) tbody tr {
      cursor: pointer;
    }

    &__pagination {
      display: flex;
      justify-content: center;
      margin-bottom: 20px;
    }

    &__checkbox--unselected {
      opacity: .4;
    }

    &__clickable-cell {
      cursor: pointer;
    }

    &__row-actions {
      opacity: 0;
      transition: opacity .2s;
    }

    tr:hover &__row-actions {
      opacity: 1;
    }

    &__pin-btn {
      opacity: 0;
      margin-left: 12px;
      border-radius: 0;

      ::v-deep(.q-focus-helper) {
        display: none !important;
      }

      &--pinned {
        opacity: 1;
      }
    }

    tr:hover &__pin-btn {
      opacity: 1;
    }

    &__kebab-btn {
      min-height: unset;
      padding: 4px;
    }

    &__kebab-menu {
      display: flex;
      flex-direction: column;

      .mi-btn {
        font-family: "Man Europe", sans-serif;
        font-weight: 400;
        font-size: 14px;
        line-height: 150%;
        align-items: start;
        width: 100%;
      }
    }

    &__description {
      white-space: normal;
    }

    .mi-btn.q-btn:not(.q-btn--flat):not(.q-btn--outline).disabled,
    .mi-btn.q-btn:not(.q-btn--flat):not(.q-btn--outline)[disabled] {
      opacity: 0 !important;
    }

    tr:hover .mi-btn.q-btn:not(.q-btn--flat):not(.q-btn--outline).disabled,
    tr:hover .mi-btn.q-btn:not(.q-btn--flat):not(.q-btn--outline)[disabled] {
      opacity: 1 !important;
    }

    .mi-btn.q-btn:not(.q-btn--flat):not(.q-btn--outline).disabled:hover,
    .mi-btn.q-btn:not(.q-btn--flat):not(.q-btn--outline)[disabled]:hover {
      background-color: $mi-silver-100 !important;
    }
  }

  ::v-deep(.mi-btn.q-btn.q-btn--flat:hover) {
    background-color: transparent !important;
  }
</style>

<style lang="scss">
  .new-configuration-dialog {
    .q-dialog__inner > .mi-card {
      width: 540px;
      max-width: 540px;
      min-width: 540px;
    }

    .new-configuration-step-description {
      background-color: $mi-anthracite-50;
      margin-left: -24px;
      margin-right: -24px;
      padding-right: 24px;
      padding-left: 24px;
    }

    h5 {
      font-size: 20px;
    }

    .mi-card__section--close-dialog {
      background-color: $mi-anthracite-50;
    }

    .mi-icon-close {
      font-size: 21px !important;
      width: 21px;
      height: 21px;
    }
  }
</style>
